import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        // Fetch related posts
        fetchRelatedPosts(data.category);
      } else {
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (category) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/posts?category=${category}&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setRelatedPosts((data.posts || []).filter(p => p.slug !== slug).slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Post not found</p>
          <Link to="/blog" className="text-amber-400 hover:text-amber-300">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/blog" className="inline-flex items-center text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <span className="text-amber-400 text-sm font-medium uppercase tracking-wide">
            {post.category || 'Article'}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-2 mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-6">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author || 'Master Steven Yong'}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.published_at || post.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.read_time || '5 min read'}
            </span>
          </div>

          {/* Share Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">Share:</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 hover:bg-blue-600 hover:text-white transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 hover:bg-sky-500 hover:text-white transition-colors"
            >
              <Twitter className="w-4 h-4" />
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${post.title}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 hover:bg-blue-700 hover:text-white transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </header>

        {/* Featured Image */}
        {post.image && (
          <div className="aspect-video rounded-2xl overflow-hidden mb-8">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-invert prose-lg max-w-none mb-12"
          style={{
            color: '#d1d5db',
            lineHeight: '1.8'
          }}
        >
          <div 
            dangerouslySetInnerHTML={{ __html: post.content || post.excerpt }}
            className="blog-content"
          />
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-gray-800 rounded-2xl p-8 text-center mb-12">
          <h3 className="text-2xl font-bold text-white mb-4">Want to Learn More?</h3>
          <p className="text-gray-400 mb-6">
            Explore our courses or book a personal consultation to dive deeper into Chinese Metaphysics.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/courses">
              <Button className="bg-amber-600 hover:bg-amber-500">
                Explore Courses
              </Button>
            </Link>
            <Link to="/consultation-fees">
              <Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section>
            <h3 className="text-2xl font-bold text-white mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-amber-500/50 transition-colors"
                >
                  <div className="aspect-video">
                    <img
                      src={relatedPost.image || 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400'}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-white font-medium group-hover:text-amber-400 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 {
          color: white;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .blog-content h2 { font-size: 1.75rem; font-weight: 700; }
        .blog-content h3 { font-size: 1.5rem; font-weight: 600; }
        .blog-content p { margin-bottom: 1.5rem; }
        .blog-content ul, .blog-content ol { margin-bottom: 1.5rem; padding-left: 1.5rem; }
        .blog-content li { margin-bottom: 0.5rem; }
        .blog-content a { color: #f59e0b; }
        .blog-content a:hover { color: #fbbf24; }
        .blog-content blockquote {
          border-left: 4px solid #f59e0b;
          padding-left: 1rem;
          font-style: italic;
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
